import type { HTMLAttributes, ReactNode } from "react";
import { AlertTriangle, Check, Info, XCircle } from "lucide-react";
import { cn } from "@/lib/cn";
import type { StatusTone } from "@/lib/types";

const TONE_STYLES: Record<StatusTone, string> = {
  success: "bg-status-success-bg text-status-success",
  warning: "bg-status-warning-bg text-status-warning",
  danger: "bg-status-danger-bg text-status-danger",
  info: "bg-status-info-bg text-status-info",
  neutral: "bg-status-neutral-bg text-status-neutral",
};

// Color is never the sole carrier of status — pair every tone with a
// distinct icon so meaning survives grayscale/colorblind viewing.
const TONE_ICONS: Record<StatusTone, ReactNode> = {
  success: <Check aria-hidden="true" size={12} strokeWidth={2.5} />,
  warning: <AlertTriangle aria-hidden="true" size={12} strokeWidth={2.5} />,
  danger: <XCircle aria-hidden="true" size={12} strokeWidth={2.5} />,
  info: <Info aria-hidden="true" size={12} strokeWidth={2.5} />,
  neutral: null,
};

export interface PillProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: StatusTone;
  icon?: ReactNode;
  /** Hide the leading icon entirely (rare — prefer keeping it for a11y). */
  hideIcon?: boolean;
}

/**
 * Status/pill system. Fully-rounded, semantic-tone badge used consistently
 * across every screen for state, counts and channel labels.
 */
export function Pill({ tone = "neutral", icon, hideIcon, className, children, ...props }: PillProps) {
  const resolvedIcon = icon ?? (hideIcon ? null : TONE_ICONS[tone]);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1.5 text-xs font-bold leading-none",
        TONE_STYLES[tone],
        className,
      )}
      {...props}
    >
      {resolvedIcon}
      {children}
    </span>
  );
}
